// apps/patient/src/components/appointments/RescheduleAppointmentPanel.tsx
// Shared by the portal reschedule page and the public reminder-link page.

import { useMemo, useState } from 'react';
import { AlertCircle, CalendarClock, Check, Clock, MapPin, Phone, User } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { AppointmentTimeField, AppointmentTimeData } from '@prism-ehr/shared';
import { patientApiService } from '../../services/api';
import {
  ManageAppointmentInfo,
  ManageEligibleProvider,
  RescheduleBody,
  WaitlistJoinBody,
  describeChange,
  formatLocationAddress,
  formatProviderName,
  readErrorMessage
} from './manageTypes';
import { JoinWaitlistPanel } from './JoinWaitlistPanel';

interface RescheduleAppointmentPanelProps {
  info: ManageAppointmentInfo;
  onSubmit: (body: RescheduleBody) => Promise<any>;
  onDone?: () => void;
  /**
   * Waitlist hand-off for the picker's no-availability notice. Only wired when
   * the org setting self_service_waitlist_on_no_availability is on (see
   * `info.settings`); absent = notice without the button.
   */
  onJoinWaitlist?: (body: WaitlistJoinBody) => Promise<any>;
}

type ProviderMode = 'current' | 'any';

const formatDateStr = (dateStr: string) =>
  new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });

const formatTimeStr = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
};

interface DoneState {
  date: string;
  startTime: string;
  endTime?: string;
  providerName: string;
  locationName: string | null;
  locationChanged: string | null;
  providerChanged: string | null;
}

export function RescheduleAppointmentPanel({ info, onSubmit, onDone, onJoinWaitlist }: RescheduleAppointmentPanelProps) {
  const currentLocationId = info.location?.id || null;
  const currentProviderName = formatProviderName(info.provider);

  const [locationId, setLocationId] = useState<string | null>(currentLocationId);
  const [mode, setMode] = useState<ProviderMode>('current');
  const [picked, setPicked] = useState<AppointmentTimeData | null>(null);
  const [pickerNonce, setPickerNonce] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<DoneState | null>(null);
  const [waitlistView, setWaitlistView] = useState(false);

  const scanAheadEnabled = info.settings?.self_service_waitlist_on_no_availability === true;

  const eligibleProviders = info.eligible_providers || [];
  const currentEligible = eligibleProviders.find(p => p.id === info.provider.id);

  // Locations the patient may pick. Same-provider-only: just the sites the current
  // provider is schedulable at (or the current site when that list is unknown).
  const locationOptions = useMemo(() => {
    const all = info.locations || [];
    if (!info.same_provider_only) return all;
    const ids = currentEligible?.schedulable_location_ids;
    if (!Array.isArray(ids)) return all.filter(l => l.id === currentLocationId);
    return all.filter(l => ids.includes(l.id));
  }, [info.locations, info.same_provider_only, currentEligible, currentLocationId]);

  const locationUnchanged = (locationId || null) === currentLocationId;

  // Providers offered at the chosen location. A provider without a schedulable
  // list is only offered when the location is unchanged.
  const providersAtLocation = useMemo<ManageEligibleProvider[]>(() => {
    if (info.same_provider_only) {
      return currentEligible ? [currentEligible] : [{ ...info.provider }];
    }
    return eligibleProviders.filter(p => {
      if (!Array.isArray(p.schedulable_location_ids)) return locationUnchanged;
      if (!locationId) return locationUnchanged;
      return p.schedulable_location_ids.includes(locationId);
    });
  }, [info.same_provider_only, eligibleProviders, currentEligible, locationId, locationUnchanged]);

  const currentProviderAtLocation = providersAtLocation.some(p => p.id === info.provider.id);
  const showProviderChoice = !info.same_provider_only && providersAtLocation.length > 1;
  const effectiveMode: ProviderMode = mode === 'current' && currentProviderAtLocation ? 'current' : 'any';
  const anyProviderIds = providersAtLocation.map(p => p.id);

  const selectedLocation = (info.locations || []).find(l => l.id === locationId) || null;

  const pickedProvider = useMemo(() => {
    if (!picked) return null;
    if (effectiveMode === 'current') return info.provider;
    return eligibleProviders.find(p => p.id === picked.provider_id) || null;
  }, [picked, effectiveMode, eligibleProviders, info.provider]);

  const pickedProviderName = pickedProvider ? formatProviderName(pickedProvider) : (picked?.provider_name || '');
  const locationChange = describeChange('Location', info.location?.name || '', selectedLocation?.name || '');
  const providerChange = describeChange('Provider', currentProviderName, pickedProviderName);

  // Same-day / next-day notice (2026-10-25): the patient's OTHER open appointments
  // arrive as YYYY-MM-DD strings; compare against the picked day and the day after.
  // Informational only, never blocks Confirm.
  const otherDates = info.other_appointment_dates || [];
  const addDays = (dateStr: string, days: number) => {
    const d = new Date(`${dateStr}T12:00:00`);
    d.setDate(d.getDate() + days);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const sameDayConflict = picked?.appointment_date ? otherDates.includes(picked.appointment_date) : false;
  const nextDayDate = picked?.appointment_date ? addDays(picked.appointment_date, 1) : null;
  const nextDayConflict = nextDayDate ? otherDates.includes(nextDayDate) : false;

  const resetPicker = () => {
    setPicked(null);
    setPickerNonce(n => n + 1);
  };

  const changeLocation = (id: string) => {
    setLocationId(id || null);
    setError(null);
    resetPicker();
  };

  const changeMode = (next: ProviderMode) => {
    setMode(next);
    setError(null);
    resetPicker();
  };

  const handleConfirm = async () => {
    if (!picked || !picked.appointment_date || !picked.start_time || !picked.start_timestamp) return;
    const providerId = effectiveMode === 'current' ? info.provider.id : picked.provider_id;
    if (!providerId) return;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        provider_id: providerId,
        location_id: locationId,
        appointment_date: picked.appointment_date,
        start_time: picked.start_time,
        start_timestamp: picked.start_timestamp,
        timezone: info.timezone
      });
      setDone({
        date: picked.appointment_date,
        startTime: picked.start_time,
        endTime: picked.end_time,
        providerName: pickedProviderName,
        locationName: selectedLocation?.name || null,
        locationChanged: locationChange,
        providerChanged: providerChange
      });
    } catch (err) {
      const message = await readErrorMessage(err, 'We could not reschedule your appointment. Please try again.');
      setError(message);
      if (/just taken/i.test(message)) {
        resetPicker();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (waitlistView && onJoinWaitlist) {
    const providerLabel = effectiveMode === 'current'
      ? currentProviderName
      : `any of ${providersAtLocation.map(formatProviderName).join(', ')}`;
    return (
      <JoinWaitlistPanel
        info={info}
        providerMode={effectiveMode}
        providerLabel={providerLabel}
        locationId={locationId}
        locationName={selectedLocation?.name || null}
        onSubmit={onJoinWaitlist}
        onBack={() => setWaitlistView(false)}
      />
    );
  }

  if (done) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-green-900 text-center mb-2">Appointment Rescheduled</h3>
        <p className="text-green-800 text-center mb-4">Your appointment has been moved to:</p>
        <div className="space-y-2 text-green-800">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            <span className="text-sm">{formatDateStr(done.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span className="text-sm">
              {formatTimeStr(done.startTime)}{done.endTime ? ` to ${formatTimeStr(done.endTime)}` : ''}
            </span>
          </div>
          {done.providerName && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="text-sm">{done.providerName}</span>
            </div>
          )}
          {done.locationName && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">{done.locationName}</span>
            </div>
          )}
        </div>
        {(done.locationChanged || done.providerChanged) && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
            {done.locationChanged && <p className="text-sm text-amber-800">{done.locationChanged}</p>}
            {done.providerChanged && <p className="text-sm text-amber-800">{done.providerChanged}</p>}
          </div>
        )}
        <div className="mt-4 text-sm text-green-800">
          <p className="font-medium">{info.organization_name}</p>
          {info.organization_phone && (
            <a href={`tel:${info.organization_phone}`} className="inline-flex items-center gap-1 underline">
              <Phone className="h-3.5 w-3.5" />
              {info.organization_phone}
            </a>
          )}
        </div>
        {onDone ? (
          <button
            type="button"
            onClick={onDone}
            className="mt-6 w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to appointment
          </button>
        ) : (
          <p className="mt-6 text-sm text-green-700 text-center">You can close this page.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current appointment */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-blue-800 mb-3">Current Appointment</h3>
        <div className="space-y-2 text-blue-700">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            <span className="text-sm">
              {formatInTimeZone(new Date(info.start_timestamp), info.timezone, 'EEEE, MMMM d, yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span className="text-sm">
              {formatTimeStr(info.start_time)}{info.end_time ? ` to ${formatTimeStr(info.end_time)}` : ''}
              {' '}{formatInTimeZone(new Date(info.start_timestamp), info.timezone, 'zzz')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="text-sm">{currentProviderName}</span>
          </div>
          {info.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">{info.location.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Location */}
      {locationOptions.length > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
          <select
            value={locationId || ''}
            onChange={(e) => changeLocation(e.target.value)}
            disabled={submitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          >
            {locationOptions.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          {formatLocationAddress(selectedLocation?.address ?? null) && (
            <p className="text-sm text-gray-500 mt-2">{formatLocationAddress(selectedLocation?.address ?? null)}</p>
          )}
        </div>
      )}

      {/* Provider */}
      {showProviderChoice && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="block text-sm font-medium text-gray-700 mb-3">Provider</p>
          <div className="space-y-3">
            <label className={`flex items-start gap-3 ${currentProviderAtLocation ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input
                type="radio"
                name="reschedule-provider-mode"
                checked={effectiveMode === 'current'}
                onChange={() => changeMode('current')}
                disabled={submitting || !currentProviderAtLocation}
                className="mt-1"
              />
              <span className="text-sm text-gray-800">
                Keep my provider ({currentProviderName})
                {!currentProviderAtLocation && (
                  <span className="block text-xs text-gray-500">Not available at this location</span>
                )}
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="reschedule-provider-mode"
                checked={effectiveMode === 'any'}
                onChange={() => changeMode('any')}
                disabled={submitting}
                className="mt-1"
              />
              <span className="text-sm text-gray-800">
                Any of these providers
                <span className="block text-xs text-gray-500">
                  {providersAtLocation.map(formatProviderName).join(', ')}
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Time picker */}
      {providersAtLocation.length === 0 ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-red-800 text-sm">
            No providers are available at this location. Please choose another location or call the office.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Select New Time</h3>
          <AppointmentTimeField
            key={`${locationId || 'none'}-${effectiveMode}-${pickerNonce}`}
            form={{ organization_id: info.organization_id } as any}
            providerId={effectiveMode === 'current' ? info.provider.id : undefined}
            providerIds={effectiveMode === 'any' ? anyProviderIds : undefined}
            providerName={effectiveMode === 'current' ? currentProviderName : undefined}
            locationId={locationId || undefined}
            value={picked}
            onChange={setPicked}
            appointmentType={info.appointment_type
              ? { id: info.appointment_type.id, name: info.appointment_type.name, duration_minutes: info.appointment_type.duration_minutes }
              : { id: '', duration_minutes: info.duration_minutes }}
            showAppointmentTypes={false}
            showProviderName={true}
            forceSingleSlot={true}
            required={true}
            disabled={submitting}
            apiService={patientApiService}
            scanAheadEnabled={scanAheadEnabled}
            onJoinWaitlist={scanAheadEnabled && onJoinWaitlist ? () => setWaitlistView(true) : undefined}
          />
        </div>
      )}

      {/* Review */}
      {picked && picked.appointment_date && picked.start_time && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Review New Appointment</h3>
          <div className="space-y-2 text-gray-800">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{formatDateStr(picked.appointment_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm">
                {formatTimeStr(picked.start_time)}{picked.end_time ? ` to ${formatTimeStr(picked.end_time)}` : ''}
              </span>
            </div>
            {pickedProviderName && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm">{pickedProviderName}</span>
              </div>
            )}
            {selectedLocation && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm">{selectedLocation.name}</span>
              </div>
            )}
          </div>

          {(locationChange || providerChange) && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
              {locationChange && <p className="text-sm text-amber-800">{locationChange}</p>}
              {providerChange && <p className="text-sm text-amber-800">{providerChange}</p>}
            </div>
          )}

          {(sameDayConflict || nextDayConflict) && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {sameDayConflict && (
                  <p className="text-sm text-amber-800">
                    You already have an appointment on {formatDateStr(picked.appointment_date)}.
                  </p>
                )}
                {nextDayConflict && nextDayDate && (
                  <p className="text-sm text-amber-800">
                    You also have an appointment the next day, {formatDateStr(nextDayDate)}.
                  </p>
                )}
                <p className="text-xs text-amber-700">You can still confirm this time if that works for you.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="mt-4 w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Rescheduling...</span>
              </>
            ) : (
              <>
                <CalendarClock className="h-4 w-4" />
                <span>Confirm new time</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Error surfaced after the pick was cleared (slot just taken) */}
      {error && !picked && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
